import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { fadeInOut } from '../../../services/animations';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ListViewViewModel } from '../../model/listViewViewModel';
import { Configuration, StandardViewModel, StandardService, StandardGroupService, StandardGroupViewModel } from '../../index';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { Observable } from 'rxjs';
import { Permission } from '../../../models/permission.model';
import { AuthService } from '../../../services/auth.service';

@Component({
    selector: 'app-standard-list',
    templateUrl: './standard-list.component.html',
    styleUrls: ['./standard-list.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StandardListComponent implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator, { static: true })
    paginator: MatPaginator;
    @ViewChild(MatSort, { static: true })
    sort: MatSort;
    standardGroupId: number = null;

    displayedColumns = ['name', 'enabled', 'sortOrder', 'createdDate', 'updatedDate', 'actions'];
    dataSource: MatTableDataSource<StandardViewModel>;
    loadingIndicator: boolean;

    // Filtering
    @Input() fixedView: boolean = false;
    currentSearchText: string;
    pageTitle: string = "Standarder för standardgrupp";

    constructor(private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private standardGroupService: StandardGroupService,
        private standardService: StandardService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute) {

        this.dataSource = new MatTableDataSource<StandardViewModel>();
        this.currentSearchText = "";
        this.standardGroupId = Number.parseInt(this.route.snapshot.paramMap.get('standardGroupId'));
    }

    ngOnInit() {
        this.loadData();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue;
    }

    private refresh() {
        // Causes the filter to refresh there by updating with recently added data.
        this.applyFilter(this.dataSource.filter);
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.standardService.getStandardsByStandardGroup(this.standardGroupId).subscribe(
            bus => this.onDataLoadSuccessful(bus),
            error => this.onDataLoadFailed(error)
        );

        this.standardGroupService.getStandardGroupById(this.standardGroupId).subscribe(
            bus => this.onStandardGroupDataLoadSuccessful(bus),
            error => this.onDataLoadFailed(error)
        );

    }

    private onDataLoadSuccessful(standards: StandardViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.dataSource.data = standards;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"), this.translationService.getTranslation("FetchErrorMessage") + "\r\n" + Utilities.getHttpResponseMessage(error),
            MessageSeverity.error, error);
    }


    confirmDelete(standard: StandardViewModel) {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            standard.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));
                this.loadingIndicator = true;

                this.standardService.deleteStandard(standard.standardId)
                    .subscribe(results => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;
                        this.alertService.showMessage(this.translationService.getTranslation("commands.Delete"),
                            standard.name + " " + this.translationService.getTranslation("globalTexts.deleted"),
                            MessageSeverity.success);
                        this.loadData();
                    },
                        error => {
                            this.alertService.stopLoadingMessage();
                            this.loadingIndicator = false;

                            let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                                ": " +
                                Utilities.getHttpResponseMessage(error);

                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("globalTexts.DeleteError"),
                                errorMessage,
                                MessageSeverity.error,
                                error);
                        });
            });
    }

    gotoStandardDetails(): void {
        if (this.standardGroupId) {
            this.router.navigate(['./standard'], { queryParams: { standardGroupId: this.standardGroupId } });
        } else {
            this.router.navigate(['./standard']);
        }
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

    hasPermissionToDelete(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

    onStandardGroupDataLoadSuccessful(standardGroupViewModel: StandardGroupViewModel): void {
        this.pageTitle = "Standarder för standardgrupp - " + standardGroupViewModel.name;
    }
}
